import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { provideFetchChartTags } from '~/api-core/Charts/FetchChartTags'

export async function useChartsTags(shouldUseUrlTags = true) {
  const { coreFetch } = useProvideCoreFetch()
  const {
    params: { slug: lastParam },
  } = useRoute()
  const [paramCountry, paramGenre] =
    typeof lastParam === 'string' ? [] : lastParam

  const stateKeySpecifier = shouldUseUrlTags
    ? 'USING_PAGE_PARAM'
    : 'NO_PAGE_PARAM'
  const selectedCountryName = useState<string | 'all'>(
    `CHARTS_PAGE_SELECTED_COUNTRY_${stateKeySpecifier}`,
    () => paramCountry || 'all',
  )
  const selectedGenreName = useState<string | 'all'>(
    `CHARTS_PAGE_SELECTED_TAG_${stateKeySpecifier}`,
    () => paramGenre || 'all',
  )
  selectedCountryName.value = shouldUseUrlTags ? paramCountry || 'all' : 'all'
  selectedGenreName.value = shouldUseUrlTags ? paramGenre || 'all' : 'all'

  const { data } = await useAsyncData(
    `GET_CHART_TAGS_${paramCountry === 'all' ? undefined : paramCountry || 'no-filter'}`,
    async () => {
      const getChartTags = provideFetchChartTags(coreFetch)
      return getChartTags(paramCountry === 'all' ? undefined : paramCountry)
    },
    {
      default: () => ({
        countries: [] as string[],
        genres: [] as string[],
      }),
      dedupe: 'defer',
      // prevent spamming this call since this gets called every time a genre/country is selected
      // and we don't want to refetch when only the genre changes or if we already have the tags data for a given country
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
    },
  )

  if (
    selectedGenreName.value !== 'all' &&
    !data.value.genres.includes(selectedGenreName.value)
  )
    throw createError({ statusCode: 404 })

  if (
    selectedCountryName.value !== 'all' &&
    !data.value.countries.includes(selectedCountryName.value)
  )
    throw createError({ statusCode: 404 })

  const tagsDataForFetch = computed(() => ({
    country:
      selectedCountryName.value === 'all'
        ? undefined
        : selectedCountryName.value,
    genre:
      selectedGenreName.value === 'all' ? undefined : selectedGenreName.value,
  }))

  const sortedAvailableCountries = computed(() => {
    // Sort the countries alphabetically
    const sorted = data.value.countries.toSorted((a, b) => a.localeCompare(b))

    // Place "US" 1st in the list
    const usTagName = 'us'
    const usIndex = sorted.indexOf(usTagName)
    if (usIndex > -1) {
      sorted.splice(usIndex, 1)
      sorted.unshift(usTagName)
    }

    // Place "france" 2nd in the list (or 1st if "US" isn't in the list)
    const franceTagName = 'france'
    const franceIndex = sorted.indexOf(franceTagName)
    if (franceIndex > -1) {
      sorted.splice(franceIndex, 1)
      const usIndex = sorted.indexOf(usTagName)
      sorted.splice(usIndex + 1, 0, franceTagName)
    }

    return sorted
  })

  const sortedAvailableGenres = computed(() => {
    // Sort the genres alphabetically
    return data.value.genres.toSorted((a, b) => a.localeCompare(b))
  })

  return {
    selectedCountryName,
    selectedGenreName,
    sortedAvailableCountries,
    sortedAvailableGenres,
    availableCountries: data.value.countries,
    availableGenres: data.value.genres,
    tagsDataForFetch,
  } as const
}
